import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectKeysParams,
  GetProjectKeysData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectKeysState
  extends MergeRequestState<GetProjectKeysData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectKeys,
  isFetching: (state: ReduxState) => state.server.getProjectKeys.isFetching,
  data: (state: ReduxState) => state.server.getProjectKeys.data,
  error: (state: ReduxState) => state.server.getProjectKeys.error,
};

const initialState: GetProjectKeysState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectKeys';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: GetProjectKeysParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjectKeys(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectKeys {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectKeys: ServerGetProjectKeys = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
